<template>
    <div class="profile-page">
        <LoadingBar v-if="loading" message="Loading Company..." />

        <div v-else class="container-xl">

            <!-- Breadcrumb -->
            <nav class="profile-breadcrumb" aria-label="breadcrumb">
                <a href="#" @click.prevent="$router.back()">
                    <i class="fa fa-arrow-left"></i> Companies
                </a>
                <span class="sep">/</span>
                <span class="current">{{ company.name || 'Company' }}</span>
            </nav>

            <!-- Header -->
            <div class="profile-header">
                <div class="profile-header-icon" style="background:var(--pp-accent-light);color:var(--pp-accent);">
                    <img v-if="company.logo_url" :src="company.logo_url" alt="Company Logo"
                        style="width:100%;height:100%;object-fit:cover;border-radius:12px;" />
                    <i v-else class="fa fa-building"></i>
                </div>
                <div class="profile-header-body">
                    <h3>{{ company.name || 'Unnamed Company' }}</h3>
                    <div class="profile-header-meta">
                        <span><i class="fa fa-hashtag"></i>{{ company.external_id || 'N/A' }}</span>
                        <span v-if="company.email"><i class="fa fa-envelope"></i>{{ company.email }}</span>
                        <span v-if="company.phone"><i class="fa fa-phone"></i>{{ company.phone }}</span>
                    </div>
                </div>
                <div class="profile-actions">
                    <span class="profile-badge" :class="statusBadgeClass">
                        <span class="dot"></span> {{ statusLabel }}
                    </span>
                    <button type="button" class="action-btn action-btn-back" @click="$router.back()">
                        <i class="fa fa-arrow-left"></i> Back
                    </button>
                    <button type="button" class="action-btn action-btn-primary" @click="openEdit">
                        <i class="fa fa-pencil"></i> Edit
                    </button>
                </div>
            </div>

            <!-- Stat bar -->
            <div class="profile-stats">
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-accent">
                        <i class="fa fa-users"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Total Users</div>
                        <div class="profile-stat-value">{{ users.length }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-success">
                        <i class="fa fa-user-check"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Active Users</div>
                        <div class="profile-stat-value">{{ activeUsers }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-info">
                        <i class="fa fa-map-marker"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Location</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ locationLabel }}
                        </div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-warning">
                        <i class="fa fa-calendar"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Created</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ formatDate(company.created_at) || 'N/A' }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main layout -->
            <div class="row g-3">

                <!-- Left: Details + Users table -->
                <div class="col-lg-8 d-flex flex-column gap-3">

                    <!-- Core details -->
                    <div class="profile-card">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-info-circle"></i> Company Details
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-field-grid">

                                <div class="profile-field" v-if="company.email">
                                    <span class="profile-label">Email</span>
                                    <span class="profile-value">
                                        <a :href="'mailto:' + company.email"
                                            style="color:var(--pp-accent);text-decoration:none;">
                                            {{ company.email }}
                                        </a>
                                    </span>
                                </div>

                                <div class="profile-field" v-if="company.phone">
                                    <span class="profile-label">Phone</span>
                                    <span class="profile-value">{{ company.phone }}</span>
                                </div>

                                <div class="profile-field" v-if="company.poc1">
                                    <span class="profile-label">Point of Contact 1</span>
                                    <span class="profile-value">{{ company.poc1 }}</span>
                                </div>

                                <div class="profile-field" v-if="company.poc2">
                                    <span class="profile-label">Point of Contact 2</span>
                                    <span class="profile-value">{{ company.poc2 }}</span>
                                </div>

                                <div class="profile-field" v-if="company.country">
                                    <span class="profile-label">Country</span>
                                    <span class="profile-value">{{ company.country }}</span>
                                </div>

                                <div class="profile-field" v-if="company.city">
                                    <span class="profile-label">City</span>
                                    <span class="profile-value">{{ company.city }}</span>
                                </div>

                                <div class="profile-field" v-if="company.created_at">
                                    <span class="profile-label">Created At</span>
                                    <span class="profile-value">{{ formatDate(company.created_at) }}</span>
                                </div>

                                <div class="profile-field" v-if="company.status">
                                    <span class="profile-label">Status</span>
                                    <span class="profile-badge" :class="statusBadgeClass">
                                        <span class="dot"></span> {{ statusLabel }}
                                    </span>
                                </div>

                            </div>

                            <!-- Address: full width -->
                            <div class="profile-field mt-3" v-if="company.address" style="grid-column:1/-1;">
                                <span class="profile-label">Address</span>
                                <span class="profile-value" style="line-height:1.6;">{{ company.address }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Users table -->
                    <div class="profile-card" v-if="users.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-accent);">
                                <i class="fa fa-users" style="color:var(--pp-accent);"></i> Users
                            </div>
                            <span class="profile-count-pill">{{ users.length }}</span>
                        </div>
                        <div class="profile-card-body p-0">
                            <div class="table-responsive">
                                <table class="profile-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Joined</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(u, i) in users" :key="u.id || i">
                                            <td>
                                                <div class="d-flex align-items-center gap-2">
                                                    <div class="profile-avatar c-accent"
                                                        style="width:30px;height:30px;font-size:12px;flex-shrink:0;">
                                                        {{ userInitials(u) }}
                                                    </div>
                                                    <span class="profile-value" style="font-size:13px;">
                                                        {{ fullName(u) }}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <a v-if="u.email" :href="'mailto:' + u.email" class="profile-value"
                                                    style="font-size:12px;color:var(--pp-accent);text-decoration:none;">{{
                                                    u.email }}</a>
                                                <span v-else class="profile-value" style="font-size:12px;">—</span>
                                            </td>
                                            <td class="profile-value" style="font-size:12px;">{{ u.phone || '—' }}</td>

                                            <td class="profile-value" style="font-size:12px;">
                                                {{ formatDate(u.created_at) || '—' }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Right: Sidebar -->
                <div class="col-lg-4 d-flex flex-column gap-3">

                    <!-- Quick info card -->
                    <div class="profile-card">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-id-card"></i> Quick Info
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="d-flex flex-column gap-2">

                                <div class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;">
                                    <div class="profile-avatar c-accent" style="width:28px;height:28px;font-size:11px;">
                                        <i class="fa fa-building" style="font-size:12px;"></i>
                                    </div>
                                    <div>
                                        <div class="profile-label" style="font-size:10px;margin-bottom:0;">Company ID
                                        </div>
                                        <div class="profile-value" style="font-size:12px;font-family:monospace;">
                                            {{ company.external_id || '—' }}
                                        </div>
                                    </div>
                                </div>

                                <div v-if="company.poc1" class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;">
                                    <div class="profile-avatar c-info" style="width:28px;height:28px;font-size:11px;">
                                        <i class="fa fa-user" style="font-size:12px;"></i>
                                    </div>
                                    <div>
                                        <div class="profile-label" style="font-size:10px;margin-bottom:0;">POC 1</div>
                                        <div class="profile-value" style="font-size:13px;">{{ company.poc1 }}</div>
                                    </div>
                                </div>

                                <div v-if="company.poc2" class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;">
                                    <div class="profile-avatar c-warning"
                                        style="width:28px;height:28px;font-size:11px;">
                                        <i class="fa fa-user" style="font-size:12px;"></i>
                                    </div>
                                    <div>
                                        <div class="profile-label" style="font-size:10px;margin-bottom:0;">POC 2</div>
                                        <div class="profile-value" style="font-size:13px;">{{ company.poc2 }}</div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                 
                    <!-- Empty connections state -->
                    <div class="profile-card" v-if="!users.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-users"></i> Users
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-empty">
                                <i class="fa fa-users"></i>
                                <p>No users yet</p>
                                <span style="font-size:12px;color:var(--pp-text-muted);">
                                    No users are linked to this company
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <!-- Edit Modal -->
        <FormModal v-if="showEditModal" ref="editModal" title="Edit Company" id="edit_company_form_modal">
            <EditCompany :company-id="company.external_id" @updated="onUpdated" @close="closeEditModal" />
        </FormModal>

    </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import EditCompany from './EditCompany.vue'

export default {
    name: 'CompanyProfile',
    components: { LoadingBar, FormModal, EditCompany },

    data() {
        return {
            company: {},
            loading: false,
            showEditModal: false,
        }
    },

    computed: {
        // Always read live from the route — never stale from data()
        external_id() {
            return this.$route.params.external_id
        },

        users() {
            // Comes from company->users() relation via user_company pivot
            return this.company.get_users || []
        },

        activeUsers() {
            return this.users.filter(u => (u.status || 'active') === 'active').length
        },

        locationLabel() {
            const parts = [this.company.city, this.company.country].filter(Boolean)
            return parts.length ? parts.join(', ') : 'N/A'
        },

        statusLabel() {
            return this.company.status
                ? this.company.status.charAt(0).toUpperCase() + this.company.status.slice(1)
                : 'Active'
        },

        statusBadgeClass() {
            const map = { active: 'c-success', inactive: 'c-danger' }
            return map[this.company.status?.toLowerCase()] ?? 'c-success'
        },
       
    },

    watch: {
        // Refetch if user navigates from one company profile to another
        external_id(newVal) {
            if (newVal) this.fetchProfile()
        },
    },

    mounted() {
        this.fetchProfile()
    },

    methods: {
        async fetchProfile() {
            this.loading = true
            const result = await form_api_submit({
                url: `companies/get/${this.external_id}`,
                method: 'GET',
                show_success_toast: false,
                show_error_toast: true,
                remove_pre_toasts: false,
            })
            if (result.success) {
                this.company = result.data
            }
            this.loading = false
        },

        openEdit() {
            this.showEditModal = true
            this.$nextTick(() => this.$refs.editModal.showModal())
        },

        closeEditModal() {
            if (this.$refs.editModal) this.$refs.editModal.closeModal()
            this.showEditModal = false
        },

        async onUpdated() {
            await this.fetchProfile()
        },

        fullName(u) {
            return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'User'
        },

        userInitials(u) {
            const f = (u.first_name || '').charAt(0).toUpperCase()
            const l = (u.last_name || '').charAt(0).toUpperCase()
            return f + l || 'U'
        },

        formatDate(date) {
            if (!date) return '—'
            try {
                return new Date(date).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric',
                })
            } catch {
                return date
            }
        },
    },
}
</script>